import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";
import { LoginCard } from "@/components/shell/LoginCard";
import { Button, buttonVariants } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

function safeNextPath(raw: string | null) {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export default function MagicLoginPage() {
  const { user, isLoading, loginWithMagicLink } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const nextPath = safeNextPath(searchParams.get("next"));

  const [status, setStatus] = useState<"pending" | "error">("pending");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading || user) return;
    if (!token) {
      setStatus("error");
      setErrorMessage("登录链接无效：缺少 token 参数");
      return;
    }

    let cancelled = false;
    loginWithMagicLink(token)
      .then(() => {
        if (!cancelled) setStatus("pending");
      })
      .catch((caughtError) => {
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(
            caughtError instanceof ApiError ? caughtError.message : "登录失败，请稍后重试",
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoading, user, token, loginWithMagicLink]);

  if (isLoading) {
    return (
      <LoginCard title="登录中" subtitle="正在确认登录状态…">
        <div className="flex justify-center py-4 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      </LoginCard>
    );
  }

  if (user) return <Navigate to={nextPath} replace />;

  if (!token || status === "error") {
    return (
      <LoginCard title="无法登录" subtitle={errorMessage ?? "登录链接无效或已失效"}>
        <div className="space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            链接可能已过期、已使用或格式不正确。请回到已登录设备重新生成登录链接。
          </p>
          <Link to="/login" className={cn(buttonVariants({ variant: "default" }), "w-full")}>
            前往登录页
          </Link>
        </div>
      </LoginCard>
    );
  }

  return (
    <LoginCard title="正在登录" subtitle="请稍候，正在验证登录链接…">
      <div className="flex flex-col items-center gap-4 py-4">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
        <Button type="button" variant="ghost" size="sm" disabled>
          验证中…
        </Button>
      </div>
    </LoginCard>
  );
}
